/**
 * The React half of the app shell.
 *
 * During the conversion this grows surface by surface - today the two corner
 * clusters; the HUD, splash, panel frame and the rest follow - while main.tsx
 * keeps driving the 3D scene and the frame loop imperatively. Everything
 * here sits under one LanguageProvider (a language switch is one re-render,
 * not a hand-written rebuild) and one ConfigProvider carrying the SimFab
 * dark theme and antd's own locale pack.
 */

import { App as AntApp, ConfigProvider } from 'antd'
import type { Journal } from '../learn/Journal'
import type { HouseBus } from '../sim/house/model'
import type { Tool } from '../fab/layout'
import type { Place } from '../fab/places'
import { LanguageProvider, useLang } from './i18n/LanguageProvider'
import { antdTheme } from './theme/antdTheme'
import { antdLocaleFor } from './theme/antdLocale'
import { TopLeftCluster } from './shell/TopLeftCluster'
import { TopRightCluster } from './shell/TopRightCluster'
import { ConfirmBridge } from './ui/confirm'
import { ShellDialogs } from './ui/ShellDialogs'
import { Hud, OpenToolButton } from './hud/Hud'
import { ToolPanelFrame } from './panel/ToolPanelFrame'
import { Splash } from './shell/Splash'
import { MissionChip } from './shell/MissionChip'
import { CopyrightFootnote } from './shell/CopyrightFootnote'
import { VrButton } from './shell/VrButton'
import { TalkPanel } from './talk/TalkPanel'

export interface ShellProps {
  jumpToTool: (target: Tool | Place) => void
  journal: Journal
  house: HouseBus
}

function ThemedShell({ jumpToTool, journal, house }: ShellProps) {
  const lang = useLang()
  return (
    <ConfigProvider theme={antdTheme} locale={antdLocaleFor(lang)}>
      <AntApp>
        <ConfirmBridge />
        <Hud />
        <OpenToolButton />
        <ToolPanelFrame />
        <TalkPanel />
        <MissionChip />
        <TopLeftCluster />
        <TopRightCluster jumpToTool={jumpToTool} journal={journal} />
        <CopyrightFootnote />
        <VrButton />
        <Splash />
        <ShellDialogs journal={journal} house={house} />
      </AntApp>
    </ConfigProvider>
  )
}

export function Shell(props: ShellProps) {
  return (
    <LanguageProvider>
      <ThemedShell {...props} />
    </LanguageProvider>
  )
}
